import { useCallback, useMemo, useState } from 'react';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';
import { TiptapOutputType } from '@amzn/awscat-editor';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { log } from '../../loggers/Logger';
import { SaveWorkingCopyError } from '../../services/Errors';
import { addPage, isWithinPageDepthLimit, listPages } from '../../services/PageOperations';
import { Page } from '../../model/Page';

/** Presentation state for the "Add new page" modal in the content navigator. */
export interface AddNewPageButtonViewModel {
  readonly isModalVisible: boolean;
  readonly title: TiptapOutputType;
  readonly isTitleValid: boolean;
  /** The node selected as the new page's parent, or `undefined` to add at the root level. */
  readonly parentPage: ContentNode | undefined;
  /** Validation error for the selected parent page (e.g. nesting depth exceeded), or `undefined` when valid. */
  readonly parentPageError: string | undefined;
  readonly isParentPageSelectionExpanded: boolean;
  /** List of pages in the content. */
  readonly pages: readonly Page[];
  /** True when all form inputs are valid and the Add button should be enabled. */
  readonly isAddEnabled: boolean;
  /** True while the new page is being saved to the server. */
  readonly isAddingPage: boolean;
  /** Inline error to display after a failed add attempt. Undefined when no error. */
  readonly addErrorMessage: string | undefined;
  readonly openModal: () => void;
  readonly dismissModal: () => void;
  /** Saves the new page and closes the modal. No-op when inputs are invalid or already saving. */
  readonly confirmAdd: () => void;
  readonly updateTitle: (newTitle: TiptapOutputType, isValidationPassed: boolean) => void;
  readonly updateParentPage: (page: ContentNode, depth: number) => void;
  readonly toggleParentPageSelection: () => void;
}

/**
 * Drives the "Add new page" modal for Guide content. Delegates page
 * creation to ContentEditorService.addPage so it saves immediately.
 */
export function useAddNewPageButtonViewModel(): AddNewPageButtonViewModel {
  const service = useContentEditorService();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [title, setTitle] = useState<TiptapOutputType>('');
  const [isTitleValid, setIsTitleValid] = useState(false);
  const [parentPage, setParentPage] = useState<ContentNode | undefined>(undefined);
  const [parentPageError, setParentPageError] = useState<string | undefined>(undefined);
  const [isParentPageSelectionExpanded, setIsParentPageSelectionExpanded] = useState(false);
  const [isAddingPage, setIsAddingPage] = useState(false);
  const [addErrorMessage, setAddErrorMessage] = useState<string | undefined>(undefined);

  const content = service.content;
  const pages = useMemo(() => (content ? listPages(content) : []), [content]);

  // When the user clicks "Add page", show the modal.
  const openModal = useCallback(() => {
    setAddErrorMessage(undefined);
    setIsModalVisible(true);
  }, []);

  // When the modal is dismissed, reset all form state.
  const dismissModal = useCallback(() => {
    setIsModalVisible(false);
    setTitle('');
    setIsTitleValid(false);
    setParentPage(undefined);
    setParentPageError(undefined);
    setIsParentPageSelectionExpanded(false);
    setIsAddingPage(false);
    setAddErrorMessage(undefined);
  }, []);

  // When the title input changes, store the new value and let the input
  // tell us whether it passes validation. Length bounds are enforced by
  // the input itself, not the view model.
  const updateTitle = useCallback((newTitle: TiptapOutputType, isValidationPassed: boolean) => {
    setTitle(newTitle);
    setIsTitleValid(isValidationPassed);
  }, []);

  // When a parent page is selected, validate the nesting depth.
  const updateParentPage = useCallback((page: ContentNode, depth: number) => {
    setParentPage(page);
    setParentPageError(!isWithinPageDepthLimit(depth) ? 'Page nesting limit reached.' : undefined);
  }, []);

  // When the parent picker is toggled, clear the selection if collapsing.
  const toggleParentPageSelection = useCallback(() => {
    if (isParentPageSelectionExpanded && parentPage) {
      setParentPage(undefined);
      setParentPageError(undefined);
    }
    setIsParentPageSelectionExpanded((prev) => !prev);
  }, [isParentPageSelectionExpanded, parentPage]);

  // When the user confirms, save the new page and close the modal.
  const confirmAdd = useCallback(() => {
    if (!isTitleValid || isAddingPage || !content) return;
    setIsAddingPage(true);
    setAddErrorMessage(undefined);
    void (async () => {
      try {
        const updated = addPage(content, title, parentPage);
        if (!updated) return;
        service.stageEdit(updated);
        await service.flush();
        dismissModal();
      } catch (error) {
        if (!(error instanceof SaveWorkingCopyError)) throw error;
        log.error({ err: error }, 'Failed to save new page to working copy');
        setAddErrorMessage(
          'There was an error adding the page. Please wait a few seconds and retry.',
        );
      } finally {
        setIsAddingPage(false);
      }
    })();
  }, [isTitleValid, isAddingPage, content, title, parentPage, service, dismissModal]);

  return {
    isModalVisible,
    title,
    isTitleValid,
    parentPage,
    parentPageError,
    isParentPageSelectionExpanded,
    pages,
    isAddEnabled: isTitleValid && !parentPageError,
    isAddingPage,
    addErrorMessage,
    openModal,
    dismissModal,
    confirmAdd,
    updateTitle,
    updateParentPage,
    toggleParentPageSelection,
  };
}
